import React, { useState, useEffect } from 'react';
import { 
  Send, History, Users, MessageSquare, 
  Loader2, CheckCircle2, AlertCircle, Clock, 
  Smartphone, Trash2 
} from 'lucide-react';
import { getMailings, createMailing, clearMailingHistory } from '../../services/api';
const MailingPage = () => {
  const [mailings, setMailings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [clearLoading, setClearLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    subject: '',
    message: '',
    status_filter: 'all'
  });

  const [notification, setNotification] = useState(null);

  useEffect(() => {
    fetchHistory();
    const interval = setInterval(fetchHistory, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchHistory = async () => {
    try {
      const data = await getMailings();
      setMailings(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClearHistory = async () => {
    if (!window.confirm('Вы уверены, что хотите полностью удалить всю историю рассылок? Это действие необратимо.')) {
      return;
    }
    setClearLoading(true);
    try {
      await clearMailingHistory();
      setMailings([]);
      setNotification({ type: 'success', text: 'История успешно очищена' });
    } catch (err) {
      setNotification({ type: 'error', text: 'Не удалось очистить историю' });
    } finally {
      setClearLoading(false);
      setTimeout(() => setNotification(null), 3000);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await createMailing({
        ...formData,
        status_filter: formData.status_filter === 'all' ? null : formData.status_filter
      });
      setNotification({ 
        type: 'success', 
        text: `Рассылка "${formData.subject}" запущена на ${res.total_contacts || 0} конт.` 
      });
      setFormData({ subject: '', message: '', status_filter: 'all' });
      fetchHistory();
    } catch (err) {
      const serverMessage = err.response?.data?.message;
      const text = serverMessage || err.message || 'Ошибка при запуске рассылки';
      setNotification({ type: 'error', text });
    } finally {
      setSending(false);
      setTimeout(() => setNotification(null), 5000);
    }
  };

  return (
    <div className="min-h-screen bg-[#0f172a] text-slate-200 p-3 sm:p-6 md:p-8">
      <div className="max-w-6xl mx-auto space-y-6 sm:space-y-10">
        
        {/* Header */}
        <div>
          
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-black tracking-tighter text-white flex items-center gap-2 sm:gap-3 italic uppercase">
            <Smartphone className="text-indigo-500 shrink-0" size={28} />
            <span>Whatsapp рассылки</span>
            </h1>

          <div className="flex items-center gap-2 mt-2 uppercase text-[10px] font-black tracking-[0.3em] text-indigo-400">
            <div className="h-1 w-6 bg-indigo-500 rounded-full" /> Система мгновенных уведомлений
          </div>
        </div>

        {notification && (
          <div className={`p-4 rounded-2xl border animate-in fade-in slide-in-from-top-4 flex items-center gap-3 font-bold text-sm ${
            notification.type === 'success' 
              ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' 
              : 'bg-rose-500/10 border-rose-500/20 text-rose-400'
          }`}>
            {notification.type === 'success' ? <CheckCircle2 size={20} /> : <AlertCircle size={20} />}
            {notification.text}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* ФОРМА СОЗДАНИЯ */}
          <div className="lg:col-span-1">
            <div className="bg-[#1e293b]/40 backdrop-blur-md p-8 rounded-[2.5rem] border border-white/5 shadow-2xl sticky top-24">
              <h3 className="text-lg font-black text-white uppercase mb-6 flex items-center gap-2">
                <Send size={18} className="text-indigo-400" /> Новая кампания
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-2 mb-2 block">Название кампании</label>
                  <input 
                    required
                    value={formData.subject}
                    onChange={e => setFormData({...formData, subject: e.target.value})}
                    placeholder="Например: Скидка 10%"
                    className="w-full bg-slate-900/50 border border-white/10 rounded-2xl px-5 py-4 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none transition-all"
                  />
                </div>

                <div>
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-2 mb-2 block">Фильтр получателей</label>
                  <select 
                    value={formData.status_filter}
                    onChange={e => setFormData({...formData, status_filter: e.target.value})}
                    className="w-full bg-slate-900/50 border border-white/10 rounded-2xl px-5 py-4 text-sm focus:outline-none appearance-none cursor-pointer"
                  >
                    <option value="all">Все контакты</option>
                    <option value="new">Только новые</option>
                    <option value="processing">В работе</option>
                    <option value="won">Успешные клиенты</option>
                    <option value="rejected">Отклоненные</option>
                  </select>
                </div>

                <div>
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-2 mb-2 block">Текст сообщения</label>
                  <textarea 
                    required
                    value={formData.message}
                    onChange={e => setFormData({...formData, message: e.target.value})}
                    placeholder="Ваше сообщение..."
                    className="w-full bg-slate-900/50 border border-white/10 rounded-2xl px-5 py-4 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none min-h-[150px] resize-none"
                  />
                </div>

                <button 
                  disabled={sending}
                  className="w-full max-w-sm mx-auto sm:max-w-none bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-700 text-white font-black py-3 sm:py-4 md:py-5 rounded-xl sm:rounded-2xl text-[9px] sm:text-[10px] uppercase tracking-[0.2em] shadow-lg shadow-indigo-500/20 transition-all flex items-center justify-center gap-2 sm:gap-3"
                >
                  {sending ? <Loader2 className="animate-spin" size={16} /> : <><Send size={14} /> Запустить рассылку</>}
                </button>
              </form>
            </div>
          </div>

          {/* ИСТОРИЯ РАССЫЛОК */}
          <div className="lg:col-span-2 space-y-6">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-4 mb-4 px-2 sm:px-4">
              <h3 className="text-base sm:text-lg font-black text-white uppercase flex items-center gap-2">
                <History size={16} className="text-indigo-400 shrink-0" /> <span>История</span>
              </h3>
              
              {mailings.length > 0 && (
                <button 
                  onClick={handleClearHistory}
                  disabled={clearLoading}
                  className="flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/20 text-rose-500 text-[10px] font-black uppercase tracking-widest transition-all disabled:opacity-50 shrink-0 self-end sm:self-auto"
                >
                  {clearLoading ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                  Очистить всё
                </button>
              )}
            </div>

            {loading ? (
              <div className="flex justify-center p-20"><Loader2 className="animate-spin text-indigo-500" size={40} /></div>
            ) : mailings.length === 0 ? (
              <div className="text-center p-20 bg-white/5 rounded-[3rem] border border-dashed border-white/10">
                <MessageSquare className="mx-auto text-slate-700 mb-4" size={48} />
                <p className="text-slate-500 font-bold uppercase text-[10px] tracking-widest">Рассылок пока не было</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {mailings.map((m) => {
                  const progress = m.total_count > 0 ? Math.round((m.sent_count / m.total_count) * 100) : 0;
                  
                  return (
                    <div key={m.id} className="bg-[#1e293b]/30 border border-white/5 p-6 rounded-[2.5rem] hover:border-white/10 transition-all group">
                      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                        <div className="space-y-3 flex-1">
                          <div className="flex items-center gap-3">
                            <h4 className="font-black text-white text-lg tracking-tight group-hover:text-indigo-400 transition-colors">
                              {m.subject}
                            </h4>
                          </div>
                          
                          <div className="flex items-center gap-4 text-[10px] font-bold uppercase tracking-tighter">
                            <span className="flex items-center gap-1 text-slate-500">
                              <Clock size={12} /> {new Date(m.created_at).toLocaleDateString()}
                            </span>
                            <span className="flex items-center gap-1 text-indigo-400">
                              <Users size={12} /> {m.sent_count} / {m.total_count} отправлено
                            </span>
                          </div>
                        </div>

                        <div className="flex flex-col items-end gap-2">
                          <div className={`px-4 py-1.5 rounded-full text-[8px] font-black uppercase tracking-[0.2em] border ${
                            m.status === 'processing' 
                              ? 'bg-amber-500/10 text-amber-500 border-amber-500/20 animate-pulse' 
                              : 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20'
                          }`}>
                            {m.status === 'processing' ? '● В процессе' : '✓ Завершено'}
                          </div>
                          {m.status === 'processing' && (
                            <span className="text-[10px] font-black text-indigo-400">{progress}%</span>
                          )}
                        </div>
                      </div>

                      {m.status === 'processing' && (
                        <div className="mt-4 h-1.5 w-full bg-slate-800 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-indigo-500 transition-all duration-500 shadow-[0_0_8px_rgba(99,102,241,0.6)]"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      )}

                      <div className="mt-5 bg-black/20 rounded-2xl p-4 border border-white/5 text-xs text-slate-400 leading-relaxed relative italic">
                        <div className="absolute top-0 left-0 w-1 h-full bg-indigo-500/40" />
                        "{m.message}"
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default MailingPage;